import React, { useState, useEffect } from 'react';

import { Divider, Paper, Grid } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import EmployeeSearch, { SingleValue } from 'components/EmployeeSearch';
import TextParseComponent from 'components/feed/TextParseComponent';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import FeedbackComment from 'screens/performanceReview/components/ContinuousFeedback/FeedbackComment';
import DateComponent from 'screens/performanceReview/components/ContinuousFeedback/common/DateComponent';
import GoalParsedSingleValue from 'screens/performanceReview/components/ContinuousFeedback/common/GoalParsedSingleOption';
import GoalParserOption from 'screens/performanceReview/components/ContinuousFeedback/common/GoalParserOption';
import SelectFeedbackType from 'screens/performanceReview/components/ContinuousFeedback/common/SelectFeedbackType';
import SelectVisibility from 'screens/performanceReview/components/ContinuousFeedback/common/SelectVisibility';
import styled from 'styled-components';
import { Button } from 'styles/admin';
import { StyledTypography, GutterCSS } from 'styles/common';

import {
  FEEDBACK_FORM_VISIBILITY,
  FEEDBACK_FORM_TYPE,
  FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
  TEAM_DURATIONS_OBJECT,
  SHARE_FEEDBACK_TYPES,
  ALERT_MESSAGES,
} from 'constants/ProjectConstants';

import { shareNewFeedback } from 'services/continuousFeedback';
import { getEditorStateFromStringifiedRawState, getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';
import { showErrorSnackbar, showInfoSnackbar } from 'services/snackbar';
import { getTeamTasksForListView } from 'services/task';

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
`;
const StyledPaperWithMinHeight = styled(StyledPaper)`
  min-height: 5rem;
`;
const StyledButton = styled(Button)`
  height: 5ch;
  margin: 0 1rem;
`;
const SectionContainer = styled.div`
  margin: 0.5rem 0 1.5rem 0;
  ${GutterCSS}
`;
const DateContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 15ch;
`;

const StyledGrid = styled(Grid)`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;
const ButtonContainer = styled.div`
  border-top: 1px solid #eaeaea;
  display: flex;
  padding: 1rem 0;
  min-height: 3rem;
  height: 8vh;
  justify-content: flex-end;
`;

const makeGoalsAsSelectOptions = ({ goals }) =>
  goals.map((goal) => ({
    label: goal.text,
    value: goal.id,
    macros: goal.macros,
    goalObject: goal,
  }));

const MsShareFeedback = ({
  loggedInEmployee,
  senderEmployee,
  feedbackDetails = {},
  showFeedback = false,
  isNew = true,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const { review, employee } = feedbackDetails;
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [goalsList, setGoalsList] = useState([]);
  const [selectedFormVisibility, setSelectedFormVisibility] = useState(FEEDBACK_FORM_VISIBILITY[0]);
  const [selectedFeedbackSentimentType, setSelectedFeedbackSentimentType] = useState(SHARE_FEEDBACK_TYPES[0]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [validForm, setValidForm] = useState(false);
  const [editorState, setEditorState] = useState(
    getEditorStateFromStringifiedRawState({
      FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
    })
  );

  const getGoalList = () => {
    const tagId = -6;
    const tagType = 'TEAM';
    const type = 'GOAL';
    const filters = [
      {
        type: 'NAME',
        value: selectedEmployee.value,
        operator: 'IN',
      },
    ];
    setLoading(true);
    getTeamTasksForListView({
      tagId,
      tagType,
      offset: 0,
      durationKey: TEAM_DURATIONS_OBJECT.lastMonth,
      filters,
      type,
      limit: 100,
      startTS: 0,
      endTS: Date.now(),
    })
      .then((res) => {
        const goals = makeGoalsAsSelectOptions({ goals: res });
        setGoalsList(goals);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(ALERT_MESSAGES.GENERIC.FAILURE));
      })
      .finally(() => setLoading(false));
  };

  const handleEmployeeChange = (option) => {
    setSelectedEmployee(option);
    setSelectedGoal(null);
  };

  const handleFormVisibilityChange = (option) => {
    setSelectedFormVisibility(option);
  };

  const handleFeedbackSentimentTypeChange = (option) => {
    setSelectedFeedbackSentimentType(option);
  };

  const handleSelectedGoalChange = (option) => {
    setSelectedGoal(option);
  };

  const handleClearForm = () => {
    setSelectedEmployee(null);
    setSelectedGoal(null);
    setEditorState(
      getEditorStateFromStringifiedRawState({
        FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
      })
    );

    onCancel();
  };
  const updateEditorState = (editorState) => {
    setEditorState(editorState);
  };

  const queryClient = useQueryClient();

  const handleShareFeedback = () => {
    const text = editorState.getCurrentContent().getPlainText('\n');
    if (text.length === 0) {
      dispatch(showInfoSnackbar(ALERT_MESSAGES.CONTINUOUS_FEEDBACK.COMMENT.EMPTY));
      return;
    }

    const data = getApiBodyFromEditorStateAndMedia(editorState, null, false, selectedFeedbackSentimentType.value);

    let body = {
      feedbackPost: data,
      feedbackType: FEEDBACK_FORM_TYPE.PROVIDED,
      visibility: selectedFormVisibility.value,
      receiverEmployeeId: selectedEmployee.id,
      requesterEmployeeId: null,
      providerEmployeeId: loggedInEmployee.id,
    };
    if (selectedGoal?.value) {
      body = {
        ...body,
        goal: selectedGoal.goalObject,
      };
    }

    shareNewFeedback({ body, receiverIds: [selectedEmployee?.id] })
      .then(() => {
        queryClient.invalidateQueries(['CFTable']);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE));
      })
      .finally(() => {
        setLoading(false);
        handleClearForm();
      });
  };

  const getPrimaryActionStatus = () => {
    if (isNew) {
      return true;
    }
    if (!review?.isArchived && loggedInEmployee.id === review?.receiverEmployeeId.toString()) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (selectedEmployee && selectedEmployee?.value) {
      getGoalList();
    }
  }, [selectedEmployee]);

  useEffect(() => {
    const editorText = editorState.getCurrentContent().getPlainText('\n');

    if (selectedEmployee?.value && editorText.length > 0) {
      setValidForm(true);
    } else {
      setValidForm(false);
    }
  }, [selectedEmployee?.id, editorState]);

  return (
    <>
      <SectionContainer>
        <StyledTypography variant="body2" bottomGutter={1}>
          Feedback for*
        </StyledTypography>
        {senderEmployee ? (
          <EmployeeSearch
            defaultValue={senderEmployee}
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        ) : null}
        {!senderEmployee ? (
          <EmployeeSearch
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        ) : null}
      </SectionContainer>
      <SectionContainer>
        {showFeedback ? (
          <Grid container spacing={8} justify="space-between">
            <Grid item sm={5}>
              <StyledTypography customColor="grey" variant="body2" bottomGutter={1}>
                Feedback visibility
              </StyledTypography>
              <StyledPaper padding="0.5rem 0.5rem" variant="outlined">
                <span>{FEEDBACK_FORM_VISIBILITY.find((item) => item.value === review.visibility).label}</span>
              </StyledPaper>
            </Grid>
            <StyledGrid item sm={6}>
              <DateContainer>
                <StyledTypography customColor="grey" variant="body2" bottomGutter={1}>
                  Date
                </StyledTypography>
                <DateComponent date={review.submittedOn} message="Submitted On" placement="right" />
              </DateContainer>
            </StyledGrid>
          </Grid>
        ) : (
          <>
            <StyledTypography variant="body2" bottomGutter={1}>
              Feedback visibility*
            </StyledTypography>
            <SelectVisibility value={selectedFormVisibility} onChange={handleFormVisibilityChange} />
          </>
        )}
      </SectionContainer>

      <SectionContainer>
        {showFeedback ? (
          <Grid container spacing={8} justify="space-between">
            <Grid item sm={5}>
              <StyledTypography color="grey" variant="body2" bottomGutter={1}>
                Feedback type
              </StyledTypography>
              <StyledPaper padding="0.5rem 0.5rem" variant="outlined">
                <span>
                  {SHARE_FEEDBACK_TYPES.find(
                    (item) => item.value === review?.feedbackPost?.macrosRes?.attributes?.sentiment
                  )?.label || 'NA'}
                </span>
              </StyledPaper>
            </Grid>
          </Grid>
        ) : (
          <>
            <StyledTypography variant="body2" bottomGutter={1}>
              Feedback type*
            </StyledTypography>
            <SelectFeedbackType value={selectedFeedbackSentimentType} onChange={handleFeedbackSentimentTypeChange} />
          </>
        )}
      </SectionContainer>

      <SectionContainer bottomGutter={2}>
        <StyledTypography variant="body2" bottomGutter={1}>
          What do you think of employees performance in past 3 months*
        </StyledTypography>
        {showFeedback ? (
          <>
            {review && review.goal ? (
              <StyledPaper padding="0.5rem 0.5rem" variant="outlined">
                <TextParseComponent text={review.goal.text} res={review.goal.macros} />
              </StyledPaper>
            ) : null}
          </>
        ) : (
          <Select
            placeholder="Add goal"
            options={goalsList}
            onChange={handleSelectedGoalChange}
            components={{
              Option: GoalParserOption,
              SingleValue: GoalParsedSingleValue,
            }}
            value={selectedGoal || null}
          />
        )}
      </SectionContainer>
      <SectionContainer topGutter={0} bottomGutter={0}>
        {showFeedback ? (
          <StyledPaperWithMinHeight padding="0.5rem 0.5rem" variant="outlined">
            <TextParseComponent text={review.feedbackPost.text} res={review.feedbackPost.macrosRes} />
          </StyledPaperWithMinHeight>
        ) : (
          <>
            <FeedbackComment
              editorState={editorState}
              updateEditorState={updateEditorState}
              customCSS={{ margin: '1rem 0', padding: '10px 5px', minHeight: '9rem' }}
            />
            <StyledTypography customColor="grey" topGutter={2} variant="caption">
              Please feel free to refer to the exhibited core competencies in your feedback via hashtags (<b>#</b>){' '}
              <br />
              eg. Jason went out his way to resolve customer onboarding issues on the Treadstone project{' '}
              <b>#CustomerFocus #JustDoIt</b>
            </StyledTypography>
          </>
        )}
      </SectionContainer>
      <Divider />
      <ButtonContainer topGutter={3}>
        <StyledButton blueOutline size="large" leftGutter={3} onClick={handleClearForm}>
          Cancel
        </StyledButton>
        {getPrimaryActionStatus() && (
          <StyledButton
            blue
            size="large"
            leftGutter={3}
            onClick={handleShareFeedback}
            disabled={loading || (showFeedback ? !!review?.isArchived : !validForm)}
          >
            Share
          </StyledButton>
        )}
      </ButtonContainer>
    </>
  );
};

export default MsShareFeedback;
